import type { NodePropertySchema } from '../../../types/node-properties';

const schema: NodePropertySchema = {
  nodeType: 'access-peoplehr',
  version: '1.0.0',
  properties: [
    {
      key: 'credentialId',
      label: 'PeopleHR Credential',
      type: 'credential',
      required: true,
      typeOptions: {
        credentialType: 'peoplehr',
      },
      description: 'Select your PeopleHR API credential',
    },
    {
      key: 'resource',
      label: 'Resource',
      type: 'options',
      required: true,
      default: 'employee',
      options: [
        { value: 'employee', name: 'Employee' },
        { value: 'absence', name: 'Absence' },
        { value: 'holiday', name: 'Holiday' },
        { value: 'salary', name: 'Salary' },
      ],
      description: 'The PeopleHR resource to interact with',
    },

    // ===== EMPLOYEE ACTIONS =====
    {
      key: 'action',
      label: 'Action',
      type: 'options',
      required: true,
      default: 'getAllEmployees',
      options: [
        { value: 'getAllEmployees', name: 'Get All Employees' },
        { value: 'getEmployeeById', name: 'Get Employee by ID' },
        { value: 'createEmployee', name: 'Create Employee' },
        { value: 'updateEmployee', name: 'Update Employee' },
        { value: 'markAsLeaver', name: 'Mark as Leaver' },
        { value: 'updateEmployeeId', name: 'Update Employee ID' },
      ],
      displayOptions: {
        show: {
          resource: ['employee'],
        },
      },
    },

    // ===== ABSENCE ACTIONS =====
    {
      key: 'action',
      label: 'Action',
      type: 'options',
      required: true,
      default: 'getAbsence',
      options: [
        { value: 'getAbsence', name: 'Get Absence' },
        { value: 'addAbsence', name: 'Add Absence' },
        { value: 'updateAbsence', name: 'Update Absence' },
        { value: 'deleteAbsence', name: 'Delete Absence' },
      ],
      displayOptions: {
        show: {
          resource: ['absence'],
        },
      },
    },

    // ===== HOLIDAY ACTIONS =====
    {
      key: 'action',
      label: 'Action',
      type: 'options',
      required: true,
      default: 'getHoliday',
      options: [
        { value: 'getHoliday', name: 'Get Holiday' },
        { value: 'addHoliday', name: 'Add Holiday' },
        { value: 'updateHoliday', name: 'Update Holiday' },
        { value: 'deleteHoliday', name: 'Delete Holiday' },
      ],
      displayOptions: {
        show: {
          resource: ['holiday'],
        },
      },
    },

    // ===== SALARY ACTIONS =====
    {
      key: 'action',
      label: 'Action',
      type: 'options',
      required: true,
      default: 'getSalary',
      options: [
        { value: 'getSalary', name: 'Get Salary' },
        { value: 'createSalary', name: 'Create Salary' },
        { value: 'deleteSalary', name: 'Delete Salary' },
      ],
      displayOptions: {
        show: {
          resource: ['salary'],
        },
      },
    },

    // ===== EMPLOYEE FIELDS =====
    {
      key: 'employeeId',
      label: 'Employee ID',
      type: 'string',
      required: true,
      placeholder: 'e.g., EMP001',
      description: 'The PeopleHR Employee ID',
      displayOptions: {
        show: {
          resource: ['employee'],
          action: ['getEmployeeById', 'createEmployee', 'updateEmployee', 'markAsLeaver'],
        },
      },
      modeToggle: {
        enabled: true,
        defaultMode: 'data',
        loadOptions: {
          method: 'getEmployees',
          cacheResults: true,
          cacheTtl: 60000,
        },
        textModeLabel: 'Expression',
        dataModeLabel: 'Select',
      },
    },
    {
      key: 'firstName',
      label: 'First Name',
      type: 'string',
      required: true,
      placeholder: 'e.g., John',
      displayOptions: {
        show: {
          resource: ['employee'],
          action: ['createEmployee'],
        },
      },
    },
    {
      key: 'firstName',
      label: 'First Name',
      type: 'string',
      required: false,
      placeholder: 'e.g., John',
      description: 'Leave empty to keep current value',
      displayOptions: {
        show: {
          resource: ['employee'],
          action: ['updateEmployee'],
        },
      },
    },
    {
      key: 'lastName',
      label: 'Last Name',
      type: 'string',
      required: true,
      placeholder: 'e.g., Smith',
      displayOptions: {
        show: {
          resource: ['employee'],
          action: ['createEmployee'],
        },
      },
    },
    {
      key: 'lastName',
      label: 'Last Name',
      type: 'string',
      required: false,
      placeholder: 'e.g., Smith',
      description: 'Leave empty to keep current value',
      displayOptions: {
        show: {
          resource: ['employee'],
          action: ['updateEmployee'],
        },
      },
    },
    {
      key: 'email',
      label: 'Email',
      type: 'string',
      required: false,
      placeholder: 'e.g., john.smith@company.com',
      displayOptions: {
        show: {
          resource: ['employee'],
          action: ['createEmployee', 'updateEmployee'],
        },
      },
    },
    {
      key: 'startDate',
      label: 'Start Date',
      type: 'string',
      required: true,
      placeholder: 'YYYY-MM-DD',
      description: 'Employee start date',
      displayOptions: {
        show: {
          resource: ['employee'],
          action: ['createEmployee'],
        },
      },
    },
    {
      key: 'department',
      label: 'Department',
      type: 'string',
      required: false,
      placeholder: 'e.g., Engineering',
      displayOptions: {
        show: {
          resource: ['employee'],
          action: ['createEmployee', 'updateEmployee'],
        },
      },
      modeToggle: {
        enabled: true,
        defaultMode: 'data',
        loadOptions: {
          method: 'getDepartments',
          cacheResults: true,
          cacheTtl: 120000,
        },
        textModeLabel: 'Expression',
        dataModeLabel: 'Select',
      },
    },
    {
      key: 'jobRole',
      label: 'Job Role',
      type: 'string',
      required: false,
      placeholder: 'e.g., Software Developer',
      displayOptions: {
        show: {
          resource: ['employee'],
          action: ['createEmployee', 'updateEmployee'],
        },
      },
      modeToggle: {
        enabled: true,
        defaultMode: 'data',
        loadOptions: {
          method: 'getJobRoles',
          cacheResults: true,
          cacheTtl: 120000,
        },
        textModeLabel: 'Expression',
        dataModeLabel: 'Select',
      },
    },
    {
      key: 'reportsTo',
      label: 'Reports To',
      type: 'string',
      required: false,
      placeholder: 'Manager Employee ID',
      displayOptions: {
        show: {
          resource: ['employee'],
          action: ['createEmployee'],
        },
      },
    },
    {
      key: 'leaverDate',
      label: 'Leaver Date',
      type: 'string',
      required: true,
      placeholder: 'YYYY-MM-DD',
      description: 'Last day of employment',
      displayOptions: {
        show: {
          resource: ['employee'],
          action: ['markAsLeaver'],
        },
      },
    },
    {
      key: 'reasonForLeaving',
      label: 'Reason for Leaving',
      type: 'string',
      required: false,
      placeholder: 'e.g., Resignation',
      displayOptions: {
        show: {
          resource: ['employee'],
          action: ['markAsLeaver'],
        },
      },
    },
    {
      key: 'oldEmployeeId',
      label: 'Old Employee ID',
      type: 'string',
      required: true,
      placeholder: 'Current Employee ID',
      displayOptions: {
        show: {
          resource: ['employee'],
          action: ['updateEmployeeId'],
        },
      },
    },
    {
      key: 'newEmployeeId',
      label: 'New Employee ID',
      type: 'string',
      required: true,
      placeholder: 'New Employee ID',
      displayOptions: {
        show: {
          resource: ['employee'],
          action: ['updateEmployeeId'],
        },
      },
    },
    {
      key: 'reasonForChange',
      label: 'Reason for Change',
      type: 'string',
      required: false,
      placeholder: 'e.g., ID policy update',
      displayOptions: {
        show: {
          resource: ['employee'],
          action: ['updateEmployeeId'],
        },
      },
    },

    // ===== ABSENCE FIELDS =====
    {
      key: 'employeeId',
      label: 'Employee ID',
      type: 'string',
      required: true,
      placeholder: 'e.g., EMP001',
      displayOptions: {
        show: {
          resource: ['absence'],
          action: ['getAbsence', 'addAbsence'],
        },
      },
    },
    {
      key: 'absenceId',
      label: 'Absence ID',
      type: 'string',
      required: true,
      placeholder: 'Absence record ID',
      displayOptions: {
        show: {
          resource: ['absence'],
          action: ['updateAbsence', 'deleteAbsence'],
        },
      },
    },
    {
      key: 'startDate',
      label: 'Start Date',
      type: 'string',
      required: true,
      placeholder: 'YYYY-MM-DD',
      displayOptions: {
        show: {
          resource: ['absence'],
          action: ['addAbsence'],
        },
      },
    },
    {
      key: 'startDate',
      label: 'Start Date',
      type: 'string',
      required: false,
      placeholder: 'YYYY-MM-DD',
      description: 'Leave empty to keep current value',
      displayOptions: {
        show: {
          resource: ['absence'],
          action: ['updateAbsence'],
        },
      },
    },
    {
      key: 'endDate',
      label: 'End Date',
      type: 'string',
      required: true,
      placeholder: 'YYYY-MM-DD',
      displayOptions: {
        show: {
          resource: ['absence'],
          action: ['addAbsence'],
        },
      },
    },
    {
      key: 'endDate',
      label: 'End Date',
      type: 'string',
      required: false,
      placeholder: 'YYYY-MM-DD',
      description: 'Leave empty to keep current value',
      displayOptions: {
        show: {
          resource: ['absence'],
          action: ['updateAbsence'],
        },
      },
    },
    {
      key: 'reason',
      label: 'Reason',
      type: 'string',
      required: false,
      placeholder: 'e.g., Flu',
      displayOptions: {
        show: {
          resource: ['absence'],
          action: ['addAbsence', 'updateAbsence'],
        },
      },
    },
    {
      key: 'absenceType',
      label: 'Absence Type',
      type: 'string',
      required: false,
      placeholder: 'e.g., Sick, Personal',
      displayOptions: {
        show: {
          resource: ['absence'],
          action: ['addAbsence'],
        },
      },
      modeToggle: {
        enabled: true,
        defaultMode: 'data',
        loadOptions: {
          method: 'getAbsenceTypes',
          cacheResults: true,
          cacheTtl: 120000,
        },
        textModeLabel: 'Expression',
        dataModeLabel: 'Select',
      },
    },

    // ===== HOLIDAY FIELDS =====
    {
      key: 'employeeId',
      label: 'Employee ID',
      type: 'string',
      required: true,
      placeholder: 'e.g., EMP001',
      displayOptions: {
        show: {
          resource: ['holiday'],
          action: ['getHoliday', 'addHoliday'],
        },
      },
    },
    {
      key: 'holidayId',
      label: 'Holiday ID',
      type: 'string',
      required: true,
      placeholder: 'Holiday record ID',
      displayOptions: {
        show: {
          resource: ['holiday'],
          action: ['updateHoliday', 'deleteHoliday'],
        },
      },
    },
    {
      key: 'startDate',
      label: 'Start Date',
      type: 'string',
      required: true,
      placeholder: 'YYYY-MM-DD',
      displayOptions: {
        show: {
          resource: ['holiday'],
          action: ['addHoliday', 'deleteHoliday'],
        },
      },
    },
    {
      key: 'startDate',
      label: 'Start Date',
      type: 'string',
      required: false,
      placeholder: 'YYYY-MM-DD',
      description: 'Filter start date or leave empty',
      displayOptions: {
        show: {
          resource: ['holiday'],
          action: ['getHoliday', 'updateHoliday'],
        },
      },
    },
    {
      key: 'endDate',
      label: 'End Date',
      type: 'string',
      required: true,
      placeholder: 'YYYY-MM-DD',
      displayOptions: {
        show: {
          resource: ['holiday'],
          action: ['addHoliday', 'deleteHoliday'],
        },
      },
    },
    {
      key: 'endDate',
      label: 'End Date',
      type: 'string',
      required: false,
      placeholder: 'YYYY-MM-DD',
      description: 'Filter end date or leave empty',
      displayOptions: {
        show: {
          resource: ['holiday'],
          action: ['getHoliday', 'updateHoliday'],
        },
      },
    },
    {
      key: 'holidayType',
      label: 'Holiday Type',
      type: 'string',
      required: false,
      placeholder: 'e.g., Annual Leave',
      displayOptions: {
        show: {
          resource: ['holiday'],
          action: ['addHoliday'],
        },
      },
      modeToggle: {
        enabled: true,
        defaultMode: 'data',
        loadOptions: {
          method: 'getHolidayTypes',
          cacheResults: true,
          cacheTtl: 120000,
        },
        textModeLabel: 'Expression',
        dataModeLabel: 'Select',
      },
    },
    {
      key: 'comments',
      label: 'Comments',
      type: 'string',
      required: false,
      placeholder: 'Additional comments',
      displayOptions: {
        show: {
          resource: ['holiday'],
          action: ['addHoliday', 'updateHoliday'],
        },
      },
    },

    // ===== SALARY FIELDS =====
    {
      key: 'employeeId',
      label: 'Employee ID',
      type: 'string',
      required: true,
      placeholder: 'e.g., EMP001',
      displayOptions: {
        show: {
          resource: ['salary'],
          action: ['getSalary', 'createSalary', 'deleteSalary'],
        },
      },
    },
    {
      key: 'salaryId',
      label: 'Salary ID',
      type: 'string',
      required: true,
      placeholder: 'Salary record ID',
      displayOptions: {
        show: {
          resource: ['salary'],
          action: ['deleteSalary'],
        },
      },
    },
    {
      key: 'effectiveDate',
      label: 'Effective Date',
      type: 'string',
      required: true,
      placeholder: 'YYYY-MM-DD',
      displayOptions: {
        show: {
          resource: ['salary'],
          action: ['createSalary'],
        },
      },
    },
    {
      key: 'amount',
      label: 'Amount',
      type: 'number',
      required: true,
      placeholder: 'e.g., 50000',
      displayOptions: {
        show: {
          resource: ['salary'],
          action: ['createSalary'],
        },
      },
    },
    {
      key: 'currency',
      label: 'Currency',
      type: 'string',
      required: false,
      default: 'GBP',
      placeholder: 'e.g., GBP, USD, EUR',
      displayOptions: {
        show: {
          resource: ['salary'],
          action: ['createSalary'],
        },
      },
    },
    {
      key: 'payFrequency',
      label: 'Pay Frequency',
      type: 'string',
      required: false,
      placeholder: 'e.g., Monthly, Weekly',
      displayOptions: {
        show: {
          resource: ['salary'],
          action: ['createSalary'],
        },
      },
    },
  ],
};

export default schema;
